import { PollModel, UserModel } from "@/models";
import { siteSettingsService } from "@/services";

export class Poll {
  /**
   * Whether a user can create a new poll.
   *
   * @param {UserModel=} user
   * @returns {boolean}
   */
  public static canCreate(user?: UserModel): boolean {
    if (!user) {
      return false;
    }

    const result =
      user.hasPermission("polls.create") ||
      user.hasPermission("polls.create-billable") ||
      user.hasPermission("polls.manage");

    return Boolean(result);
  }

  /**
   * Whether a user can delete a poll.
   *
   * @param {UserModel=} user
   * @param {PollModel=} poll
   * @returns {boolean}
   */
  public static canDelete(user?: UserModel, poll?: PollModel): boolean {
    return this.canManage(user, poll);
  }

  /**
   * Whether a user can export users who have voted on a poll.
   *
   * @param {UserModel=} user
   * @returns {boolean}
   */
  public static canExportVoters(user?: UserModel): boolean {
    if (!user) {
      return false;
    }
    return user.hasPermission("polls.export-voters");
  }

  /**
   * Whether a user can export vote results for a poll.
   *
   * @param {UserModel=} user
   * @param {PollModel=} poll
   * @returns {boolean}
   */
  public static canExportVotes(user?: UserModel, poll?: PollModel): boolean {
    if (!user) {
      return false;
    }

    // if user owns the poll
    if (poll && poll.data.user_id === user.data.id) {
      return true;
    }

    return user.hasPermission("polls.manage");
  }

  /**
   * Whether a user can manage an existing poll.
   *
   * @param {PromotionModel=} poll
   * @param {UserModel=} user
   * @returns {boolean}
   */
  public static canManage(user?: UserModel, poll?: PollModel): boolean {
    if (!user || !poll) {
      return false;
    }

    // users can manage their own polls
    if (poll.data.user_id === user.data.id) {
      return true;
    }

    if (user.hasPermission("polls.manage")) {
      return true;
    }

    return false;
  }

  /**
   * Whether a user can manage the status field of a poll.
   *
   * @param {UserModel=} user
   * @returns {boolean}
   */
  public static canManageStatus(user?: UserModel): boolean {
    if (!user) {
      return false;
    }
    return user.hasPermission("polls.manage");
  }

  /**
   * Whether the user can manage a polls' options.
   *
   * @param {UserModel=} user
   * @param {PollModel=} poll
   * @returns {boolean}
   */
  public static canManageOptions(user?: UserModel, poll?: PollModel): boolean {
    if (!user) {
      return false;
    }

    if (poll && !poll.data.id) {
      return true;
    }

    return user.hasPermission("polls.manage");
  }

  /**
   * Whether payment is required to create a new poll.
   *
   * @param {UserModel=} user
   * @returns {boolean}
   */
  public static createPaymentRequired(user?: UserModel): boolean {
    if (!user) {
      return false;
    }

    const userHasPermission =
      user && user.hasPermission("polls.create-billable");
    const hasPrice = siteSettingsService.get("poll_charge_price") > 0;

    return userHasPermission && hasPrice;
  }

  /**
   * Whether the user can create both polls & events
   */
  public static canCreateEventPolls(user?: UserModel): boolean {
    if (!user) {
      return false;
    }

    const result =
      (user.hasPermission("polls.create") ||
        user.hasPermission("polls.manage")) &&
      (user.hasPermission("events.create") ||
        user.hasPermission("events.manage"));

    return Boolean(result);
  }
}

export default Poll;
